import type { CampaignChangeEvent, CampaignChangeHistory } from "@adpilot/shared";
import { csvDocument } from "@/components/campaign/ad-groups/ad-groups-table-model";
import { type CampaignChangeHistoryParams, fetchCampaignChangeHistory } from "@/lib/campaign-api";

export type ChangeHistoryExportFormat = "csv" | "xlsx";

/** The API's largest page; an export walks every page of the current filters. */
const EXPORT_PAGE_SIZE = 200;

export async function fetchAllCampaignChanges(
  campaignId: string,
  params: Omit<CampaignChangeHistoryParams, "page" | "pageSize">,
  signal?: AbortSignal,
): Promise<{ meta: Omit<CampaignChangeHistory, "items">; items: CampaignChangeEvent[] }> {
  let items: CampaignChangeEvent[] = [];
  for (let page = 1; ; page++) {
    const res = await fetchCampaignChangeHistory(
      campaignId,
      { ...params, page, pageSize: EXPORT_PAGE_SIZE },
      signal,
    );
    items = items.concat(res.data.items);
    if (!res.pagination.hasMore) {
      const { items: _last, ...meta } = res.data;
      return { meta, items };
    }
  }
}

/** Header plus one row per change, every cell already as shown in the table. */
export function changeHistorySheet(
  header: readonly string[],
  items: readonly CampaignChangeEvent[],
  cells: (event: CampaignChangeEvent) => readonly string[],
): string[][] {
  return [[...header], ...items.map((event) => [...cells(event)])];
}

export async function downloadChangeHistory(
  format: ChangeHistoryExportFormat,
  baseName: string,
  rows: readonly (readonly string[])[],
): Promise<void> {
  if (format === "csv") {
    downloadBlob(
      `${baseName}.csv`,
      new Blob([csvDocument(rows)], { type: "text/csv;charset=utf-8;" }),
    );
    return;
  }
  // Loaded on demand: only an export needs the workbook writer. Cells stay
  // strings, so nothing in them is ever evaluated as a formula.
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const blob = await writeXlsxFile(rows.map((row) => [...row])).toBlob();
  downloadBlob(`${baseName}.xlsx`, blob);
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // Firefox/Safari may start the download after click() returns; revoke on the next tick.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
